import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import RequiredMark from '@/temu-platform/components/RequiredMark';
import CategoryCascaderSelect from '@/temu-platform/components/CategoryCascaderSelect';
import { SpecValuePicker } from '@/temu-platform/modules/products/components/SpecSupplySection';
import TemuOfficialSizeChartPanel, {
  fetchSizeChartClassRequired,
} from '@/temu-platform/modules/products/components/TemuOfficialSizeChartPanel';
import {
  buildOfficialSizeChartTemplatePayload,
  isOfficialSizeChartTemplatePayload,
  normalizeOfficialSizeChartDraft,
  saveOfficialSizeChartAsReusableTemplate,
  officialSizeChartRecordsHaveSizeNames,
  validateOfficialSizeChartDraft,
} from '@/temu-platform/utils/temuOfficialSizeChart';
import {
  enrichTemuSizeChartClass,
  TEMU_SIZE_CHART_CLASS_OPTIONS,
} from '@/temu-platform/utils/temuSizeChartClassCatalog';
import { flattenLeafCategories, formatTemplateCategoryLabel } from '@/temu-platform/utils/temuCategoryTree';
import {
  attributeDisplayLabel,
  parseAttributeTemplate,
} from '@/temu-platform/utils/temuAttributeTemplate';
import { normalizeTemuAttrsToEditorShape } from '@/temu-platform/utils/normalizeTemuAttrsForEditor';
import TemuSwitch from '@/temu-platform/components/TemuSwitch';
import { nextTemplatePriority } from '@/temu-platform/utils/temuTemplatePriority';
import {
  conflictToastMessage,
  isTemplateVersionConflictError,
  parseTemplateVersion,
} from '@/temu-platform/utils/temuTemplateVersionGuard';
import TemplateVersionConflictMark from '@/temu-platform/modules/products/components/TemplateVersionConflictMark';
import { useTemuShopOptional } from '@/temu-platform/context/TemuShopContext';
import { temuPlatformAPI } from '@/utils/api';
import { adminAPI } from '@/utils/adminApi';
import { showToast } from '@/utils/toast';
import { chartText } from '@/i18n/chartText';
import { useTranslation } from 'react-i18next';

function optionName(opt) {
  return String(opt?.name || opt?.value || opt?.label || '').trim();
}

function matchSizeIdsFromLabels(sizeValues, labels = []) {
  const want = new Set(
    (labels || []).map((n) => String(n || '').trim().toLowerCase()).filter(Boolean),
  );
  if (!want.size) return [];
  return (sizeValues || [])
    .filter((v) => want.has(optionName(v).toLowerCase()))
    .map((v) => String(v.id));
}

function resolveSelectedSizeIds({ ids = [], labels = [], sizeValues = [] }) {
  const cleanedIds = (ids || []).map((id) => String(id || '').trim()).filter(Boolean);
  if (cleanedIds.length) {
    const allowed = new Set((sizeValues || []).map((v) => String(v.id)));
    if (!allowed.size) return cleanedIds;
    return cleanedIds.filter((id) => allowed.has(String(id)));
  }
  return matchSizeIdsFromLabels(sizeValues, labels);
}

function fetchSizeChartDetail(officialMode, shopId, templateId, templateScope) {
  if (officialMode) {
    return adminAPI.getOfficialTemuSizeChartTemplate(templateId);
  }
  return temuPlatformAPI.getSizeChartTemplate(shopId, templateId, { scope: templateScope });
}

function fetchSizeChartList(officialMode, shopId, params) {
  if (officialMode) {
    return adminAPI.listOfficialTemuSizeChartTemplates(params);
  }
  return temuPlatformAPI.listSizeChartTemplates(shopId, params);
}

function saveSizeChartTemplate(officialMode, shopId, templateId, body) {
  if (officialMode) {
    return templateId
      ? adminAPI.updateOfficialTemuSizeChartTemplate(templateId, body)
      : adminAPI.createOfficialTemuSizeChartTemplate(body);
  }
  return templateId
    ? temuPlatformAPI.updateSizeChartTemplate(shopId, templateId, body)
    : temuPlatformAPI.createSizeChartTemplate(shopId, body);
}

/**
 * 新建/编辑尺码库：对齐发品页官方尺码表（class/meta/settings/create）。
 */
export default function SizeChartTemplateCreateModal({
  open,
  shopId: shopIdProp = '',
  templateId = null,
  templateScope = 'PERSONAL',
  officialMode = false,
  shopType = '',
  referenceShopId = '',
  onClose,
  onSaved,
}) {
  const isEdit = Boolean(templateId);
  const temuShopCtx = useTemuShopOptional();
  const shopId = String(
    shopIdProp || referenceShopId || temuShopCtx?.activeShop?.id || '',
  ).trim();
  const officialSizeChartRef = useRef(null);
  const pendingSeedRef = useRef(null);
  /** 编辑回填：仅有尺码名时，等平台规格加载后再映射为 valueId */
  const pendingSizeLabelsRef = useRef([]);
  /** 避免把 officialDraft 放进拉 class 的 effect 依赖（onDraftChange 每次新对象会触发无限更新） */
  const officialDraftRef = useRef(null);

  useTranslation('workbench');
  const [name, setName] = useState('');
  const [styleNo, setStyleNo] = useState('');
  const [remark, setRemark] = useState('');
  const [autoRuleEnabled, setAutoRuleEnabled] = useState(false);
  const [sortOrder, setSortOrder] = useState(0);
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [productTypeId, setProductTypeId] = useState('');
  const [categoryPathLabel, setCategoryPathLabel] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [metaLoading, setMetaLoading] = useState(false);
  const [templateVersion, setTemplateVersion] = useState(null);
  const [versionConflict, setVersionConflict] = useState(false);
  const [reloadingVersion, setReloadingVersion] = useState(false);

  const [sizeChartRequired, setSizeChartRequired] = useState(false);
  const [sizeChartClass, setSizeChartClass] = useState(null);
  const [sizeChartClassLoading, setSizeChartClassLoading] = useState(false);
  const [sizeChartClassError, setSizeChartClassError] = useState('');
  const [selectedSizeIds, setSelectedSizeIds] = useState([]);
  const [fallbackSizeLabels, setFallbackSizeLabels] = useState([]);
  const fallbackSizeLabelsRef = useRef([]);
  fallbackSizeLabelsRef.current = fallbackSizeLabels;
  const [sizeAttr, setSizeAttr] = useState(null);
  const sizeAttrRef = useRef(null);
  sizeAttrRef.current = sizeAttr;
  const [attrLoading, setAttrLoading] = useState(false);
  const [officialDraft, setOfficialDraft] = useState(null);
  officialDraftRef.current = officialDraft;

  const categoryOptions = useMemo(
    () => flattenLeafCategories(categories).map((opt) => ({
      value: opt.categoryId,
      label: opt.label,
      productTypeId: opt.productTypeId,
    })),
    [categories],
  );

  useEffect(() => {
    if (!open) return;
    pendingSeedRef.current = null;
    pendingSizeLabelsRef.current = [];
    setName('');
    setStyleNo('');
    setRemark('');
    setAutoRuleEnabled(false);
    setSortOrder(0);
    setCategoryId('');
    setProductTypeId('');
    setCategoryPathLabel('');
    setSelectedSizeIds([]);
    setFallbackSizeLabels([]);
    setSizeAttr(null);
    setAttrLoading(false);
    setOfficialDraft(null);
    setDetailLoading(false);
    setSaving(false);
    setTemplateVersion(null);
    setVersionConflict(false);
    setReloadingVersion(false);
    setSizeChartRequired(false);
    setSizeChartClass(null);
    setSizeChartClassLoading(false);
  }, [open, templateId]);

  useEffect(() => {
    if (!open || (!officialMode && !shopId)) {
      setCategories([]);
      return undefined;
    }
    let cancelled = false;
    setMetaLoading(true);
    const load = officialMode
      ? adminAPI.getOfficialTemuCatalogCategories({ shopType })
      : temuPlatformAPI.getShopCategories(shopId);
    load
      .then((res) => {
        if (cancelled) return;
        setCategories(res?.categories ?? res?.items ?? res?.list ?? res ?? []);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      })
      .finally(() => {
        if (!cancelled) setMetaLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, shopId, officialMode, shopType]);

  useEffect(() => {
    if (!open || !templateId || (!officialMode && !shopId)) return undefined;
    let cancelled = false;
    setDetailLoading(true);
    fetchSizeChartDetail(officialMode, shopId, templateId, templateScope)
      .then((res) => {
        if (cancelled) return;
        const item = res?.item || res || {};
        const payload = item.payload && typeof item.payload === 'object' ? item.payload : {};
        setName(String(item.name || '').trim());
        setStyleNo(String(item.styleNo || '').trim());
        setRemark(String(item.remark || '').trim());
        setAutoRuleEnabled(item.autoRuleEnabled === true || item.autoRuleEnabled === 1);
        {
          const order = Number(item.sortOrder);
          setSortOrder(Number.isFinite(order) ? order : 0);
        }
        setCategoryId(String(item.categoryId || payload.categoryId || ''));
        setProductTypeId(String(item.productTypeId || payload.productTypeId || item.categoryId || payload.categoryId || ''));
        {
          const rawLabel = String(item.categoryPathLabel || payload.categoryPathLabel || '').trim();
          setCategoryPathLabel(rawLabel && !/^\d+$/.test(rawLabel) ? rawLabel : '');
        }
        setTemplateVersion(parseTemplateVersion(item.version));
        setVersionConflict(false);
        pendingSeedRef.current = payload;
        if (isOfficialSizeChartTemplatePayload(payload)) {
          const labels = Array.isArray(payload.selectedSizeLabels)
            ? payload.selectedSizeLabels.map((n) => String(n || '').trim()).filter(Boolean)
            : [];
          const ids = Array.isArray(payload.selectedSizeIds)
            ? payload.selectedSizeIds.map((id) => String(id || '').trim()).filter(Boolean)
            : [];
          const nextIds = resolveSelectedSizeIds({
            ids,
            labels,
            sizeValues: sizeAttrRef.current?.values || [],
          });
          setSelectedSizeIds(nextIds);
          pendingSizeLabelsRef.current = nextIds.length ? [] : labels;
          setFallbackSizeLabels(labels);
          const nextDraft = payload.officialSizeChart && typeof payload.officialSizeChart === 'object'
            ? normalizeOfficialSizeChartDraft(payload.officialSizeChart, payload.platformRaw)
            : normalizeOfficialSizeChartDraft(null, payload.platformRaw);
          setOfficialDraft(nextDraft);
          // 已有落库尺码明细时，用 payload 里的 sizeClass，避免先卡在「加载尺码规则」
          if (nextDraft?.sizeClass?.classId) {
            setSizeChartClass(enrichTemuSizeChartClass(nextDraft.sizeClass));
            setSizeChartRequired(true);
            setSizeChartClassLoading(false);
            setSizeChartClassError('');
          }
        } else {
          // 旧 SHEIN 矩阵模板：尽量从 sizeRows / sizeChart keys 还原尺码名
          const rows = Array.isArray(payload.sizeRows) ? payload.sizeRows : [];
          const fromRows = rows
            .map((row) => String(row?.name || row?.id || '').trim())
            .filter(Boolean);
          const fromKeys = Object.keys(payload.sizeChart || {})
            .map(String)
            .filter(Boolean);
          const labels = fromRows.length ? fromRows : fromKeys;
          const nextIds = resolveSelectedSizeIds({
            labels,
            sizeValues: sizeAttrRef.current?.values || [],
          });
          setSelectedSizeIds(nextIds);
          pendingSizeLabelsRef.current = nextIds.length ? [] : labels;
          setFallbackSizeLabels(labels);
          setOfficialDraft(null);
          showToast(chartText('sizeCreate.oldMatrix', '该模板为旧版尺寸矩阵格式，请按官方尺码表重新填写后保存'), 'warning');
        }
      })
      .catch((e) => {
        if (cancelled) return;
        showToast(e?.message || chartText('photoLib.loadTemplateFailed', fixedUi("加载模板失败")), 'error');
        onClose?.();
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, shopId, templateId, templateScope, officialMode, onClose]);

  // 类目 ID 有、路径名为空时：用已加载类目树补全展示名（不显示裸 ID）
  useEffect(() => {
    if (!open || !categoryId) return;
    if (categoryPathLabel && !/^\d+$/.test(String(categoryPathLabel).trim())) return;
    const hit = categoryOptions.find((opt) => String(opt.value) === String(categoryId));
    const label = String(hit?.label || formatTemplateCategoryLabel({
      categoryId,
      productTypeId,
      categoryPathLabel,
    }) || '').trim();
    if (label && !/^\d+$/.test(label)) {
      setCategoryPathLabel(label);
      if (!productTypeId && hit?.productTypeId) {
        setProductTypeId(String(hit.productTypeId));
      }
    }
  }, [open, categoryId, productTypeId, categoryPathLabel, categoryOptions]);

  useEffect(() => {
    if (!open || !(officialMode ? shopType : shopId) || !categoryId) {
      // 编辑态已有落库尺码时不要清掉 sizeClass（避免面板退回加载中）
      const draft = officialDraftRef.current;
      if (!(isEdit && draft?.records?.length)) {
        setSizeChartRequired(false);
        setSizeChartClass(null);
        setSizeChartClassError('');
        setSizeChartClassLoading(false);
      }
      return undefined;
    }
    // 本地草稿已带 sizeClass + records：跳过 class.get，避免久等
    // 注意：用 ref 读 draft，切勿把 officialDraft 放进 deps——面板 onDraftChange 每次新对象会套娃红屏
    const draft = officialDraftRef.current;
    if (
      isEdit
      && draft?.records?.length
      && Number(draft?.sizeClass?.classId || sizeChartClass?.classId || 0) > 0
    ) {
      setSizeChartRequired(true);
      if (!sizeChartClass?.classId && draft.sizeClass?.classId) {
        setSizeChartClass(enrichTemuSizeChartClass(draft.sizeClass));
      }
      setSizeChartClassLoading(false);
      setSizeChartClassError('');
      return undefined;
    }
    let cancelled = false;
    setSizeChartClassLoading(true);
    setSizeChartClassError('');
    fetchSizeChartClassRequired(shopId, categoryId, { officialMode, shopType })
      .then((res) => {
        if (cancelled) return;
        if (!res?.ok) {
          setSizeChartRequired(false);
          setSizeChartClass(null);
          setSizeChartClassError(res?.error || res?.message || chartText('templateEditor.sizeClassFailShort', '尺码表分类查询失败'));
          showToast(res?.error || res?.message || chartText('templateEditor.sizeClassFail', '尺码表分类查询失败，请重试'), 'error');
          return;
        }
        setSizeChartRequired(Boolean(res.required));
        const nextClass = enrichTemuSizeChartClass(res.sizeClass);
        // class.get 有推荐分类时预填；用户已选手动分类则保留
        setSizeChartClass((prev) => {
          if (Number(prev?.classId) > 0 && !res.required) return prev;
          if (Number(prev?.classId) > 0 && Number(prev.classId) === Number(nextClass?.classId)) {
            return enrichTemuSizeChartClass({ ...prev, ...nextClass });
          }
          return nextClass || prev;
        });
        setSizeChartClassError('');
        if (!res.required) {
          showToast(chartText('sizeCreate.noChartNeeded', '该类目平台判定无需尺码表，仍可保存站内模板草稿'), 'info');
        }
      })
      .finally(() => {
        if (!cancelled) setSizeChartClassLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 故意不依赖 officialDraft / sizeChartClass：面板 onDraftChange 与 fetch 回写会套娃红屏
  }, [open, shopId, categoryId, officialMode, shopType, isEdit]);

  /** 面板每次 emit 新对象；引用不变则跳过，打断 setOfficialDraft → 重渲染 → 再 emit 的环 */
  const handleOfficialDraftChange = useCallback((draft) => {
    setOfficialDraft((prev) => {
      if (!draft) return prev;
      // 面板短暂空表时不要冲掉已加载的测量值
      const prevHasValues = Array.isArray(prev?.records) && prev.records.some((row) => {
        const values = row?.values && typeof row.values === 'object' ? row.values : {};
        return Object.keys(values).some((k) => {
          if (k === '1' || k === 'sizeName' || k === 'groupId' || k === 'name') return false;
          return values[k] != null && String(values[k]).trim() !== '';
        });
      });
      const nextHasValues = Array.isArray(draft?.records) && draft.records.some((row) => {
        const values = row?.values && typeof row.values === 'object' ? row.values : {};
        return Object.keys(values).some((k) => {
          if (k === '1' || k === 'sizeName' || k === 'groupId' || k === 'name') return false;
          return values[k] != null && String(values[k]).trim() !== '';
        });
      });
      const nextHasSizeRows = officialSizeChartRecordsHaveSizeNames(
        draft?.records,
        draft?.groupList || prev?.groupList || [],
      );
      if (prevHasValues && !nextHasValues && !nextHasSizeRows) return prev;
      if (
        prev
        && prev.mode === draft.mode
        && prev.sizeClass === draft.sizeClass
        && prev.selectedLibraryId === draft.selectedLibraryId
        && prev.records === draft.records
        && prev.groupList === draft.groupList
        && prev.elementList === draft.elementList
        && prev.selectedBusinessIds === draft.selectedBusinessIds
      ) {
        return prev;
      }
      return draft;
    });
  }, []);

  /** 从平台属性模板加载尺码规格选项（与发品页规格供应一致） */
  useEffect(() => {
    if (!open || !(officialMode ? shopType : shopId) || !categoryId) {
      setSizeAttr(null);
      setAttrLoading(false);
      return undefined;
    }
    let cancelled = false;
    setAttrLoading(true);
    setSizeAttr(null);
    const load = officialMode
      ? adminAPI.getOfficialTemuCatalogAttributeTemplate({
        shopType,
        catId: categoryId,
        categoryId,
        productTypeId,
      }).then((raw) => normalizeTemuAttrsToEditorShape(raw, { catId: categoryId }))
      : temuPlatformAPI.getAttributeTemplate(shopId, { catId: categoryId, categoryId });
    load
      .then((res) => {
        if (cancelled) return;
        const parsed = parseAttributeTemplate(res, { productTypeId });
        const nextSizeAttr = parsed?.skuSaleAttrs?.[0] || null;
        setSizeAttr(nextSizeAttr);
        const values = nextSizeAttr?.values || [];
        setSelectedSizeIds((prev) => {
          if (prev.length) {
            const allowed = new Set(values.map((v) => String(v.id)));
            const kept = prev.filter((id) => allowed.has(String(id)));
            if (kept.length) return kept;
            // 发品页落库的 valueId 与当前类目规格对不上时，按尺码名重绑，避免标签一直显示 1940 且测量表被冲空
            const labels = (fallbackSizeLabelsRef.current || [])
              .map((n) => String(n || '').trim())
              .filter(Boolean);
            if (labels.length && values.length) {
              pendingSizeLabelsRef.current = [];
              return matchSizeIdsFromLabels(values, labels);
            }
            return prev;
          }
          const pending = pendingSizeLabelsRef.current || [];
          if (pending.length) {
            pendingSizeLabelsRef.current = [];
            return matchSizeIdsFromLabels(values, pending);
          }
          return prev;
        });
      })
      .catch((e) => {
        if (cancelled) return;
        setSizeAttr(null);
        showToast(e?.message || chartText('sizeCreate.loadSpecFail', '加载平台尺码规格失败'), 'warning');
      })
      .finally(() => {
        if (!cancelled) setAttrLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, shopId, categoryId, productTypeId, officialMode, shopType]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  const sizeOptions = useMemo(
    () => (sizeAttr?.values || []).map((v) => ({ ...v, id: String(v.id) })),
    [sizeAttr],
  );
  const sizeLabelByKey = useMemo(() => {
    const map = {};
    sizeOptions.forEach((opt) => {
      map[String(opt.id)] = optionName(opt);
    });
    // 规格选项加载中：用落库尺码名按序贴到已选 id，避免标签显示成 1940/1941…
    if (fallbackSizeLabels.length && selectedSizeIds.length) {
      selectedSizeIds.forEach((id, index) => {
        const key = String(id);
        if (map[key]) return;
        const label = String(fallbackSizeLabels[index] || '').trim();
        if (label) map[key] = label;
      });
    }
    return map;
  }, [sizeOptions, selectedSizeIds, fallbackSizeLabels]);
  const sizeLabel = attributeDisplayLabel(sizeAttr) || fixedUi("尺码");
  const selectedSizeLabels = useMemo(() => {
    const fromIds = selectedSizeIds
      .map((id) => sizeLabelByKey[String(id)] || '')
      .map((n) => String(n || '').trim())
      .filter(Boolean);
    if (fromIds.length) return fromIds;
    // 规格选项尚未加载时，用落库尺码名撑起尺寸表
    return (fallbackSizeLabels || []).map((n) => String(n || '').trim()).filter(Boolean);
  }, [selectedSizeIds, sizeLabelByKey, fallbackSizeLabels]);

  if (!open) return null;

  const onCategoryChange = (id, meta) => {
    const nextCategoryId = id ? String(id) : '';
    const hit = categoryOptions.find((opt) => String(opt.value) === nextCategoryId);
    // 店铺/官方类目缓存只存一级，更深末级由级联懒加载返回，此时 categoryOptions 里查不到该叶子，
    // 必须用级联回传的 meta 兜底，否则会误报「缺少 product_type_id」
    const metaTypeId = meta?.productTypeId != null && String(meta.productTypeId) !== '0'
      ? String(meta.productTypeId)
      : '';
    // TEMU 无独立商品类型，末级 catId 即 productTypeId（与类目缓存/发品页一致）
    const nextTypeId = hit?.productTypeId && String(hit.productTypeId) !== '0'
      ? String(hit.productTypeId)
      : (metaTypeId || nextCategoryId);
    setCategoryId(nextCategoryId);
    setProductTypeId(nextTypeId);
    setCategoryPathLabel(hit?.label || meta?.label || '');
    setOfficialDraft(null);
    setSelectedSizeIds([]);
    setFallbackSizeLabels([]);
    pendingSizeLabelsRef.current = [];
    setSizeAttr(null);
    setSizeChartClass(null);
    setSizeChartRequired(false);
    setSizeChartClassError('');
    if (!isEdit && nextTypeId && (officialMode || shopId)) {
      fetchSizeChartList(officialMode, shopId, {
        scope: templateScope,
        productTypeId: nextTypeId,
        page: 1,
        pageSize: 100,
        ...(officialMode ? { shopType } : {}),
      }).then((res) => {
        setSortOrder(nextTemplatePriority(res?.items));
      }).catch(() => {
        setSortOrder(0);
      });
    }
  };

  const applySizeChartItem = (item) => {
    const payload = item.payload && typeof item.payload === 'object' ? item.payload : {};
    setName(String(item.name || '').trim());
    setStyleNo(String(item.styleNo || '').trim());
    setRemark(String(item.remark || '').trim());
    setAutoRuleEnabled(item.autoRuleEnabled === true || item.autoRuleEnabled === 1);
    {
      const order = Number(item.sortOrder);
      setSortOrder(Number.isFinite(order) ? order : 0);
    }
    setCategoryId(String(item.categoryId || payload.categoryId || ''));
    setProductTypeId(String(item.productTypeId || payload.productTypeId || item.categoryId || payload.categoryId || ''));
    {
      const rawLabel = String(item.categoryPathLabel || payload.categoryPathLabel || '').trim();
      setCategoryPathLabel(rawLabel && !/^\d+$/.test(rawLabel) ? rawLabel : '');
    }
    setTemplateVersion(parseTemplateVersion(item.version));
    if (isOfficialSizeChartTemplatePayload(payload)) {
      const labels = (Array.isArray(payload.selectedSizeLabels) ? payload.selectedSizeLabels : [])
        .map((n) => String(n || '').trim())
        .filter(Boolean);
      const ids = (Array.isArray(payload.selectedSizeIds) ? payload.selectedSizeIds : [])
        .map((id) => String(id || '').trim())
        .filter(Boolean);
      const nextIds = resolveSelectedSizeIds({
        ids,
        labels,
        sizeValues: sizeAttrRef.current?.values || [],
      });
      setSelectedSizeIds(nextIds);
      pendingSizeLabelsRef.current = nextIds.length ? [] : labels;
      setFallbackSizeLabels(labels);
      const nextDraft = payload.officialSizeChart && typeof payload.officialSizeChart === 'object'
        ? normalizeOfficialSizeChartDraft(payload.officialSizeChart, payload.platformRaw)
        : normalizeOfficialSizeChartDraft(null, payload.platformRaw);
      setOfficialDraft(nextDraft);
      if (nextDraft?.sizeClass?.classId) {
        setSizeChartClass(enrichTemuSizeChartClass(nextDraft.sizeClass));
        setSizeChartRequired(true);
        setSizeChartClassLoading(false);
        setSizeChartClassError('');
      }
    }
  };

  const reloadTemplateFromServer = async () => {
    if (!templateId || (!officialMode && !shopId)) return;
    setReloadingVersion(true);
    try {
      const res = await fetchSizeChartDetail(officialMode, shopId, templateId, templateScope);
      applySizeChartItem(res?.item || res || {});
      setVersionConflict(false);
      showToast(chartText('sizeCreate.reloaded', '已重新加载尺码库'), 'success');
    } catch (e) {
      showToast(e?.message || chartText('photoLib.reloadFailed', fixedUi("重新加载失败")), 'error');
    } finally {
      setReloadingVersion(false);
    }
  };

  const submit = async () => {
    if (!officialMode && !shopId) {
      showToast(chartText('returnList.pickShop', fixedUi("请先选择店铺")), 'error');
      return;
    }
    if (officialMode && !shopType) {
      showToast(chartText('sizeCreate.needShopTypeFirst', fixedUi("请先选择店铺类型")), 'error');
      return;
    }
    const styleNoValue = String(styleNo || '').trim();
    if (!styleNoValue) {
      showToast(chartText('photoLib.needStyleNo', fixedUi("请填写款号")), 'error');
      return;
    }
    const title = String(name || '').trim();
    if (!title) {
      showToast(chartText('photoLib.needName', fixedUi("请填写模板名称")), 'error');
      return;
    }
    if (!productTypeId || !categoryId) {
      showToast(chartText('rrpLib.needCategory', fixedUi("请选择类目")), 'error');
      return;
    }
    if (!(Number(sizeChartClass?.classId) > 0)) {
      showToast(chartText('sizeCreate.pickClass', '请选择尺码表分类'), 'error');
      return;
    }
    if (sizeChartClassLoading) {
      showToast(chartText('sizeCreate.rulesLoading', '尺码表规则加载中，请稍后再保存'), 'warning');
      return;
    }
    if (sizeChartClassError) {
      showToast(sizeChartClassError, 'error');
      return;
    }
    if (!selectedSizeIds.length) {
      showToast(chartText('sizeCreate.pickFromPlatform', `请从平台选项中选择${sizeLabel}`, { label: sizeLabel === '尺码' ? chartText('sizeChartPanel.sizeCol', '尺码') : sizeLabel }), 'error');
      return;
    }

    let draft = officialDraft;
    if (sizeChartRequired) {
      const sizeCheck = officialSizeChartRef.current?.validate?.()
        || validateOfficialSizeChartDraft({
          required: true,
          mode: draft?.mode || 'create',
          sizeClass: sizeChartClass,
          selectedBusinessIds: draft?.selectedBusinessIds || [],
          records: draft?.records || [],
          groupList: draft?.groupList || [],
          elementList: draft?.elementList || [],
        });
      if (!sizeCheck.ok) {
        showToast(sizeCheck.message || chartText('templateEditor.fillSizeChart', '请完善尺码表'), 'error');
        return;
      }
      draft = {
        mode: draft?.mode || 'create',
        sizeClass: sizeChartClass,
        selectedBusinessIds: draft?.selectedBusinessIds || [],
        selectedLibraryId: draft?.selectedLibraryId || '',
        records: draft?.records || [],
        groupList: draft?.groupList || [],
        elementList: draft?.elementList || [],
      };
    }

    setSaving(true);
    try {
      let platformBusinessIds = Array.isArray(draft?.selectedBusinessIds)
        ? draft.selectedBusinessIds.map(Number).filter((id) => id > 0)
        : [];

      if (
        shopId
        && sizeChartRequired
        && draft?.mode === 'create'
        && !platformBusinessIds.length
      ) {
        const saved = await saveOfficialSizeChartAsReusableTemplate({
          shopId,
          catId: categoryId,
          sizeClass: sizeChartClass,
          chartName: title,
          records: draft.records || [],
          groupList: draft.groupList || [],
          elementList: draft.elementList || [],
          isDoubleSize: Boolean(draft?.isDoubleSize),
          api: temuPlatformAPI,
        });
        if (saved?.businessId) {
          platformBusinessIds = [Number(saved.businessId)];
          draft = {
            ...draft,
            mode: 'template',
            selectedBusinessIds: platformBusinessIds,
            selectedLibraryId: String(saved.businessId),
          };
          showToast(chartText('sizeCreate.syncedBiz', `已同步到平台尺码表库（#${saved.businessId}）`, { id: saved.businessId }), 'success');
        }
      }

      const payload = buildOfficialSizeChartTemplatePayload({
        categoryId,
        productTypeId,
        categoryPathLabel,
        selectedSizeLabels,
        selectedSizeIds,
        sizeClass: sizeChartClass,
        officialSizeChart: draft,
        platformBusinessIds,
      });

      const body = {
        name: title,
        styleNo: styleNoValue,
        remark: String(remark || '').trim() || undefined,
        productTypeId,
        categoryId,
        categoryPathLabel,
        autoRuleEnabled,
        sortOrder: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
        sizeSaleAttributeId: '',
        payload,
      };
      if (officialMode) {
        body.shopType = shopType;
      }
      if (!isEdit && !officialMode) {
        body.scope = templateScope;
      }
      if (isEdit && templateVersion != null) {
        body.version = templateVersion;
      }
      const res = await saveSizeChartTemplate(officialMode, shopId, isEdit ? templateId : null, body);
      if (res?.item && parseTemplateVersion(res.item.version) != null) {
        setTemplateVersion(parseTemplateVersion(res.item.version));
      }
      setVersionConflict(false);
      showToast(isEdit ? chartText('photoLib.updated', fixedUi("模板已更新")) : chartText('photoLib.createdTpl', fixedUi("模板已创建")), 'success');
      onSaved?.();
      onClose?.();
    } catch (e) {
      if (isEdit && isTemplateVersionConflictError(e)) {
        showToast(conflictToastMessage([{ type: 'sizeChart' }]), 'warning', 3000);
        setVersionConflict(true);
      } else {
        showToast(e?.message || (isEdit ? chartText('photoLib.updateFailed', fixedUi("更新失败")) : chartText('photoLib.createFailed', '创建失败')), 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="tpo-batch-modal tpo-size-tpl-modal" role="presentation">
      <button
        type="button"
        className="tpo-batch-modal__backdrop"
        aria-label={chartText('moduleShell.close', fixedUi("关闭"))}
        onClick={() => { if (!saving) onClose?.(); }}
      />
      <div
        className="tpo-batch-modal__panel tpo-size-tpl-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpo-size-tpl-create-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tpo-batch-modal__head">
          <h2 id="tpo-size-tpl-create-title">
            {isEdit
              ? (officialMode ? chartText('sizeCreate.editOfficial', '编辑官方尺码模板') : chartText('sizeCreate.editLib', '编辑尺码库'))
              : (officialMode ? chartText('sizeCreate.createOfficial', '新建官方尺码模板') : chartText('sizeCreate.createLib', '新建尺码库'))}
          </h2>
          <button type="button" className="tpo-batch-modal__close" aria-label={chartText('moduleShell.close', fixedUi("关闭"))} disabled={saving} onClick={onClose}>×</button>
        </div>
        <div className="tpo-batch-modal__body">
          <div className="tpo-wizard__form tpo-template-create-form">
            <label className="tpo-field tpo-field--full">
              <span className="tpo-field__label">{chartText('productTemplate.colStyleNo', fixedUi("款号"))} <RequiredMark /></span>
              <input
                className="tpo-input"
                value={styleNo}
                disabled={saving || detailLoading}
                maxLength={128}
                placeholder={chartText('photoLib.styleNoPh', fixedUi("请输入款号"))}
                onChange={(e) => setStyleNo(e.target.value)}
              />
            </label>
            <label className={`tpo-field tpo-field--full${versionConflict ? ' tpo-field--version-conflict' : ''}`}>
              <span className="tpo-field__label">
                {fixedUi("模板名称")} <RequiredMark />
                <TemplateVersionConflictMark
                  active={versionConflict}
                  label={chartText('photoLib.updatedMark', '已更新')}
                  reloading={reloadingVersion}
                  onReload={reloadTemplateFromServer}
                />
              </span>
              <input
                className="tpo-input"
                value={name}
                disabled={saving || detailLoading}
                maxLength={128}
                placeholder={chartText('sizeCreate.namePh', '如：女装上衣基础尺码表')}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="tpo-field tpo-field--full">
              <span className="tpo-field__label">{chartText('productTemplate.colRemark', fixedUi("备注"))}</span>
              <input
                className="tpo-input"
                value={remark}
                disabled={saving || detailLoading}
                maxLength={200}
                placeholder={chartText('photoLib.optional', fixedUi("可选"))}
                onChange={(e) => setRemark(e.target.value)}
              />
            </label>
            <div className="tpo-field tpo-field--full tpo-template-auto-rule">
              <div className="tpo-template-auto-rule__switch">
                <TemuSwitch
                  checked={autoRuleEnabled}
                  disabled={saving || detailLoading}
                  ariaLabel={chartText('productTemplate.colAutoRule', '自动规则')}
                  onChange={setAutoRuleEnabled}
                />
                <span>{chartText('productTemplate.colAutoRule', fixedUi("自动规则"))}</span>
              </div>
              <p className="tpo-wizard__inline-hint">
                {chartText('sizeCreate.autoHint', '开启后，发品选中该类目时自动带出。优先级仅在同类目内比较，数字越小越优先；同类目不可重复，不同类目互不影响。')}
              </p>
              <label className="tpo-field">
                <span className="tpo-field__label">{chartText('productTemplate.colPriority', fixedUi("优先级"))}</span>
                <input
                  className="tpo-input"
                  type="number"
                  step={1}
                  value={sortOrder}
                  disabled={saving || detailLoading}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    setSortOrder(Number.isFinite(next) ? next : 0);
                  }}
                />
              </label>
            </div>
            <label className="tpo-field tpo-field--full">
              <span className="tpo-field__label">{chartText('rrpLib.categoryApply', fixedUi("适用类目"))} <RequiredMark /></span>
              <CategoryCascaderSelect
                shopId={officialMode ? undefined : shopId}
                officialShopType={officialMode ? shopType : undefined}
                categories={categories}
                value={categoryId}
                label={categoryPathLabel}
                disabled={saving || detailLoading || metaLoading || (!officialMode && !shopId)}
                placeholder={
                  officialMode
                    ? (metaLoading || detailLoading ? chartText('templateEditor.loadingCats', '加载类目中…') : chartText('rrpLib.categorySearch', fixedUi("请选择或搜索类目")))
                    : (!shopId ? chartText('returnList.pickShop', fixedUi("请先选择店铺")) : (metaLoading || detailLoading ? chartText('tiktokPublish.loading', fixedUi("加载中…")) : chartText('rrpLib.categorySearch', fixedUi("请选择或搜索类目"))))
                }
                onChange={onCategoryChange}
              />
            </label>

            <label className="tpo-field tpo-field--full">
              <span className="tpo-field__label">{chartText('sizeCreate.classLabel', fixedUi("尺码表分类"))} <RequiredMark /></span>
              <select
                className="tpo-select"
                value={sizeChartClass?.classId ? String(sizeChartClass.classId) : ''}
                disabled={saving || detailLoading || !categoryId}
                onChange={(e) => {
                  const nextId = Number(e.target.value);
                  if (!(nextId > 0)) {
                    setSizeChartClass(null);
                    return;
                  }
                  const hit = TEMU_SIZE_CHART_CLASS_OPTIONS.find((opt) => Number(opt.classId) === nextId);
                  setSizeChartClass(enrichTemuSizeChartClass({
                    classId: nextId,
                    className: hit?.className || '',
                    classType: sizeChartClass?.classType,
                  }));
                  setSizeChartClassError('');
                }}
              >
                <option value="">
                  {!categoryId
                    ? chartText('sizeChartPanel.pickCategory', fixedUi("请先选择类目"))
                    : (sizeChartClassLoading ? chartText('sizeCreate.classLoading', '加载平台推荐分类中…') : chartText('sizeCreate.pickClass', '请选择尺码表分类'))}
                </option>
                {TEMU_SIZE_CHART_CLASS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{`${opt.classId} · ${fixedUi(opt.className)}`}</option>
                ))}
              </select>
              <p className="tpo-wizard__inline-hint">
                {chartText('sizeCreate.classHint', '平台创建尺码表需传 classId；选类目后会尝试预填推荐分类，可手动调整。')}
              </p>
            </label>

            {!officialMode && !shopId ? (
              <p className="tpo-wizard__inline-hint">
                {chartText('sizeCreate.noShopRules', '当前无可用店铺，无法拉取平台尺码规则。请先在 Temu 工作台选择店铺后再编辑。')}
              </p>
            ) : null}

            <div className="tpo-field tpo-field--full">
              <span className="tpo-field__label">{sizeLabel === '尺码' ? chartText('sizeChartPanel.sizeCol', fixedUi("尺码")) : sizeLabel} <RequiredMark /></span>

              <SpecValuePicker
                options={sizeOptions}
                value={selectedSizeIds}
                onChange={(ids) => setSelectedSizeIds((ids || []).map(String))}
                labelByKey={sizeLabelByKey}
                placeholder={
                  !categoryId
                    ? chartText('sizeChartPanel.pickCategory', fixedUi("请先选择类目"))
                    : (attrLoading ? chartText('sizeCreate.loadingPlatformSizes', '加载平台尺码中…') : chartText('sizeCreate.pickNamed', `请选择${sizeLabel}`, { label: sizeLabel === '尺码' ? chartText('sizeChartPanel.sizeCol', '尺码') : sizeLabel }))
                }
                disabled={saving || detailLoading || attrLoading || !categoryId || !sizeAttr}
                emptyText={chartText('sizeCreate.noSizeOptions', '该类目暂无平台尺码选项')}
                disabledHint={
                  !categoryId
                    ? chartText('sizeChartPanel.pickCategory', fixedUi("请先选择类目"))
                    : (attrLoading
                      ? chartText('tiktokPublish.loading', fixedUi("加载中…"))
                      : (!sizeAttr ? chartText('sizeCreate.noSizeSpec', `当前类目无${sizeLabel}规格`, { label: sizeLabel === '尺码' ? chartText('sizeChartPanel.sizeCol', '尺码') : sizeLabel }) : chartText('sizeChartPanel.notSelectable', '暂不可选')))
                }
                shopId={shopId}
                categoryId={categoryId}
                attributeId={sizeAttr?.attributeId || ''}
                attributeLabel={sizeLabel}
              />
            </div>

            {sizeChartClassError ? (
              <p className="tpo-wizard__inline-error" role="alert">{sizeChartClassError}</p>
            ) : null}
            {(() => {
              const draftHasContent = Boolean(
                officialDraft
                && Array.isArray(officialDraft.records)
                && officialDraft.records.length > 0,
              );
              const canShowPanel = (
                (categoryId && Number(sizeChartClass?.classId || officialDraft?.sizeClass?.classId || 0) > 0
                  && (officialMode ? shopType : shopId) && !sizeChartClassError)
                || (isEdit && draftHasContent && (officialMode ? shopType : shopId))
              );
              if (!canShowPanel) return null;
              return (
                <TemuOfficialSizeChartPanel
                  key={`official-size-tpl-${officialMode ? shopType : shopId}-${categoryId || 'nocat'}-${sizeChartClass?.classId || ''}-${templateId || 'new'}`}
                  ref={officialSizeChartRef}
                  shopId={officialMode ? '' : shopId}
                  officialMode={officialMode}
                  shopType={shopType}
                  categoryId={categoryId}
                  selectedSizeLabels={selectedSizeLabels}
                  required={sizeChartRequired || draftHasContent}
                  sizeClass={sizeChartClass || officialDraft?.sizeClass || null}
                  loading={draftHasContent ? false : sizeChartClassLoading}
                  initialDraft={officialDraft}
                  onDraftChange={handleOfficialDraftChange}
                  hideLibraryActions
                  emptySizeHint={chartText('sizeChartPanel.createEmpty', `请先选择上方${sizeLabel}，再填写对应测量属性。`, { label: sizeLabel })}
                />
              );
            })()}
          </div>
        </div>
        <div className="tpo-batch-modal__foot">
          <button type="button" className="tpo-btn tpo-btn--ghost" disabled={saving} onClick={onClose}>
            {chartText('shop.cancel', '取消')}
          </button>
          <button
            type="button"
            className="tpo-btn tpo-btn--primary"
            disabled={saving || detailLoading || metaLoading}
            onClick={submit}
          >
            {saving ? chartText('tiktokPublish.saving', fixedUi("保存中…")) : (isEdit ? chartText('shop.save', fixedUi("保存")) : chartText('sizeCreate.create', fixedUi("创建")))}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
